import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import {
  useGetPvxPartnerDashboardsQuery,
  useGetPvxPartnerProfileQuery,
  useUpdatePvxPartnerDashboardsMutation,
  useUpdatePvxPartnerFilterEntitlementsMutation,
  useUpdatePvxPartnerProfileMutation,
} from '@api/pvxPartners';
import {
  PvxPartnerDashboardsResponse,
  PVX_PARTNERS_TYPE,
  PvxPartnerProfileApiResponse,
  FilterEntitlementEntry,
} from '@api/types/pvxPartners';
import { PvxPartnerDetailsFormData } from '../types/partnerDetails';
import { DashboardSelectorSubmoduleItem } from '../types/dashboardSelector';
import { PartnerEntityType } from '../constants';

interface EditPvxPartnerParams {
  partnerDetails: PvxPartnerDetailsFormData;
  submodules: DashboardSelectorSubmoduleItem[];
  appAccess: FilterEntitlementEntry;
  companyAccess: FilterEntitlementEntry;
}

type UseEditPvxPartnerParams = {
  clientCode: string | undefined;
  onSuccess: () => void;
};

type UseEditPvxPartnerResult = {
  dashboardData: PvxPartnerDashboardsResponse | undefined;
  initialPartnerDetails: PvxPartnerDetailsFormData | undefined;
  isLoading: boolean;
  isSaving: boolean;
  handleEdit: (params: EditPvxPartnerParams) => Promise<void>;
};

export const useEditPvxPartner = ({
  clientCode,
  onSuccess,
}: UseEditPvxPartnerParams): UseEditPvxPartnerResult => {
  const dispatch = useDispatch();

  const {
    data: dashboardsResponse,
    isLoading: isLoadingDashboards,
    isFetching: isFetchingDashboards,
  } = useGetPvxPartnerDashboardsQuery(clientCode!, { skip: !clientCode });

  const {
    data: profileResponse,
    isLoading: isLoadingProfile,
    isFetching: isFetchingProfile,
  } = useGetPvxPartnerProfileQuery(clientCode!, { skip: !clientCode });

  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdatePvxPartnerProfileMutation();
  const [updateDashboards, { isLoading: isUpdatingDashboards }] =
    useUpdatePvxPartnerDashboardsMutation();
  const [updateFilterEntitlements, { isLoading: isUpdatingEntitlements }] =
    useUpdatePvxPartnerFilterEntitlementsMutation();

  const dashboardData = dashboardsResponse?.data;

  const initialPartnerDetails = useMemo<PvxPartnerDetailsFormData | undefined>(() => {
    const profile = profileResponse?.data as PvxPartnerProfileApiResponse | undefined;
    if (!profile) return undefined;
    return {
      entityType: profile.entityType as PartnerEntityType,
      name: profile.name,
      address: profile.address ?? '',
      email: profile.ownerEmail ?? '',
      username: profile.ownerUsername ?? '',
      partnerType: profile.partnerType,
      pvxPocEmail: profile.pvxPocEmail ?? '',
    };
  }, [profileResponse]);

  const handleEdit = async ({
    partnerDetails,
    submodules,
    appAccess,
    companyAccess,
  }: EditPvxPartnerParams) => {
    if (!clientCode) return;

    // Step 1: Update partner profile.
    try {
      await updateProfile({
        clientCode,
        name: partnerDetails.name,
        address: partnerDetails.address,
        partnerType: partnerDetails.partnerType as PVX_PARTNERS_TYPE,
        entityType: partnerDetails.entityType,
        pvxPocEmail: partnerDetails.pvxPocEmail,
      }).unwrap();
    } catch {
      dispatch(
        setToastMessage({ type: TOAST_TYPES.ERROR, message: 'Failed to update partner details' }),
      );
      return;
    }

    // Step 2: Update dashboards.
    try {
      await updateDashboards({
        clientCode,
        submoduleIds: submodules.map((s) => s.submoduleId),
      }).unwrap();
    } catch {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Partner details saved, but dashboard changes could not be updated.',
        }),
      );
      return;
    }

    // Step 3: Update filter entitlements.
    try {
      await updateFilterEntitlements({ clientCode, filters: appAccess }).unwrap();
      await updateFilterEntitlements({ clientCode, filters: companyAccess }).unwrap();
    } catch {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Partner details saved, but access changes could not be updated.',
        }),
      );
      return;
    }

    dispatch(
      setToastMessage({ type: TOAST_TYPES.SUCCESS, message: 'Details updated successfully' }),
    );
    onSuccess();
  };

  return {
    dashboardData,
    initialPartnerDetails,
    isLoading: isLoadingDashboards || isFetchingDashboards || isLoadingProfile || isFetchingProfile,
    isSaving: isUpdatingProfile || isUpdatingDashboards || isUpdatingEntitlements,
    handleEdit,
  };
};
